'use server'

import { apiRequest, ApiResponse } from '../request'
import { TissGuide } from './guide-api'

export type BillingBatchStatus =
    | 'BATCH_DRAFT'
    | 'BATCH_OPEN'
    | 'BATCH_CLOSED'
    | 'BATCH_SUBMITTED'
    | 'BATCH_RECEIVED'
    | 'BATCH_PROCESSING'
    | 'BATCH_PROCESSED'
    | 'BATCH_PARTIAL_RETURN'
    | 'BATCH_PARTIALLY_PAID'
    | 'BATCH_COMPLETED'

export interface BillingBatch {
    id: number
    companyId: number
    company?: {
        id: number
        name: string
    }
    healthPlanId: number
    healthPlan?: {
        id: number
        name: string
        ansCode: string
    }
    batchNumber: string
    name?: string
    competenceMonth: number
    competenceYear: number
    referenceMonth?: string
    status: BillingBatchStatus
    totalGuides: number
    totalValue: number
    totalGloss?: number
    totalGlossed?: number
    totalPaid?: number
    dueDate?: string
    closedAt?: string
    submittedAt?: string
    submissionDate?: string
    processedAt?: string
    protocolNumber?: string
    submissionProtocol?: string
    paymentDate?: string
    xmlFileUrl?: string
    returnFileUrl?: string
    notes?: string
    createdAt: string
    updatedAt: string
    guides?: TissGuide[]
    _count?: {
        guides: number
    }
}

export interface CreateBillingBatchData {
    healthPlanId: number
    competenceMonth: number
    competenceYear: number
    guideIds?: number[]
}

export interface BillingBatchFilters {
    healthPlanId?: number
    status?: BillingBatchStatus
    competenceMonth?: number
    competenceYear?: number
}

export interface ProcessReturnData {
    protocolNumber?: string
    returnFileUrl?: string
    totalPaid?: number
    totalGlossed?: number
    paymentDate?: string
    glosses?: Array<{
        guideId: number
        glossValue: number
        glossCode: string
        glossDescription: string
    }>
}

// Batch CRUD
export async function getBillingBatches(
    filters?: BillingBatchFilters
): Promise<ApiResponse<BillingBatch[]>> {
    return apiRequest<BillingBatch[]>('billing/batches', { params: filters })
}

export async function getBillingBatch(
    id: number
): Promise<ApiResponse<BillingBatch>> {
    return apiRequest<BillingBatch>(`billing/batches/${id}`, {})
}

// Alias for convenience
export const getBatch = getBillingBatch

export async function createBillingBatch(
    data: CreateBillingBatchData
): Promise<ApiResponse<BillingBatch>> {
    return apiRequest<BillingBatch>('billing/batches', { data }, 'post')
}

// Alias for convenience
export const createBatch = createBillingBatch

export interface UpdateBillingBatchData {
    guideIds?: number[]
    notes?: string
}

export async function updateBillingBatch(
    id: number,
    data: UpdateBillingBatchData
): Promise<ApiResponse<BillingBatch>> {
    return apiRequest<BillingBatch>(`billing/batches/${id}`, { data }, 'patch')
}

// Alias for convenience
export const updateBatch = updateBillingBatch

// Available guides
export async function getAvailableGuides(
    healthPlanId: number
): Promise<ApiResponse<TissGuide[]>> {
    return apiRequest<TissGuide[]>(
        `billing/batches/available-guides/${healthPlanId}`,
        {}
    )
}

// Add/Remove guides
export async function addGuidesToBatch(
    batchId: number,
    guideIds: number[]
): Promise<ApiResponse<BillingBatch>> {
    return apiRequest<BillingBatch>(
        `billing/batches/${batchId}/add-guides`,
        { data: { guideIds } },
        'post'
    )
}

export async function removeGuideFromBatch(
    batchId: number,
    guideId: number
): Promise<ApiResponse<BillingBatch>> {
    return apiRequest<BillingBatch>(
        `billing/batches/${batchId}/guides/${guideId}`,
        {},
        'delete'
    )
}

// Workflow
export async function closeBatch(
    id: number
): Promise<ApiResponse<BillingBatch>> {
    return apiRequest<BillingBatch>(`billing/batches/${id}/close`, {}, 'post')
}

export async function reopenBatch(
    id: number
): Promise<ApiResponse<BillingBatch>> {
    return apiRequest<BillingBatch>(`billing/batches/${id}/reopen`, {}, 'post')
}

export async function submitBatch(
    id: number,
    xmlFileUrl?: string
): Promise<ApiResponse<BillingBatch>> {
    return apiRequest<BillingBatch>(
        `billing/batches/${id}/submit`,
        { data: { xmlFileUrl } },
        'post'
    )
}

export async function processReturn(
    id: number,
    data: ProcessReturnData
): Promise<ApiResponse<BillingBatch>> {
    return apiRequest<BillingBatch>(
        `billing/batches/${id}/process-return`,
        { data },
        'post'
    )
}

export async function acknowledgeBatch(
    id: number
): Promise<ApiResponse<BillingBatch>> {
    return apiRequest<BillingBatch>(
        `billing/batches/${id}/acknowledge`,
        {},
        'post'
    )
}

export async function processBatchResponse(
    id: number,
    data: ProcessReturnData
): Promise<ApiResponse<BillingBatch>> {
    return processReturn(id, data)
}

// Alias for convenience
export const addGuideToBatch = addGuidesToBatch

export async function completeBatch(
    id: number,
    totalPaid: number
): Promise<ApiResponse<BillingBatch>> {
    return apiRequest<BillingBatch>(
        `billing/batches/${id}/complete`,
        { data: { totalPaid } },
        'post'
    )
}

// XML generation
export async function generateBatchXml(id: number): Promise<ApiResponse<string>> {
    return apiRequest<string>(`billing/batches/${id}/xml`, {})
}

// Labels
export const BATCH_STATUS_LABELS: Record<BillingBatchStatus, string> = {
    BATCH_DRAFT: 'Rascunho',
    BATCH_OPEN: 'Aberto',
    BATCH_CLOSED: 'Fechado',
    BATCH_SUBMITTED: 'Enviado',
    BATCH_RECEIVED: 'Recebido',
    BATCH_PROCESSING: 'Processando',
    BATCH_PROCESSED: 'Processado',
    BATCH_PARTIAL_RETURN: 'Retorno Parcial',
    BATCH_PARTIALLY_PAID: 'Parcialmente Pago',
    BATCH_COMPLETED: 'Concluido',
}

export const BATCH_STATUS_COLORS: Record<BillingBatchStatus, string> = {
    BATCH_DRAFT: 'badge-ghost',
    BATCH_OPEN: 'badge-info',
    BATCH_CLOSED: 'badge-warning',
    BATCH_SUBMITTED: 'badge-info',
    BATCH_RECEIVED: 'badge-success',
    BATCH_PROCESSING: 'badge-warning',
    BATCH_PROCESSED: 'badge-success',
    BATCH_PARTIAL_RETURN: 'badge-warning',
    BATCH_PARTIALLY_PAID: 'badge-warning',
    BATCH_COMPLETED: 'badge-success',
}

// Helper to format competence period
export function formatCompetence(month: number, year: number): string {
    const months = [
        'Janeiro',
        'Fevereiro',
        'Marco',
        'Abril',
        'Maio',
        'Junho',
        'Julho',
        'Agosto',
        'Setembro',
        'Outubro',
        'Novembro',
        'Dezembro',
    ]
    return `${months[month - 1]}/${year}`
}

// Helper to get competence options
export function getCompetenceOptions(): Array<{
    month: number
    year: number
    label: string
}> {
    const options = []
    const now = new Date()
    const currentYear = now.getFullYear()
    const currentMonth = now.getMonth() + 1

    // Last 12 months + next 2 months
    for (let i = -12; i <= 2; i++) {
        let month = currentMonth + i
        let year = currentYear

        while (month <= 0) {
            month += 12
            year -= 1
        }
        while (month > 12) {
            month -= 12
            year += 1
        }

        options.push({
            month,
            year,
            label: formatCompetence(month, year),
        })
    }

    return options.sort((a, b) => {
        if (a.year !== b.year) return b.year - a.year
        return b.month - a.month
    })
}
